import { Link } from 'react-router'
import { Hourglass, Palette, Plus } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { FlowRail } from './flow-rail'

interface Props {
  variant: 'first-run' | 'no-sources'
  /** no-sources only: a moodboard exists but none are ready yet. */
  hasAnyMoodboard?: boolean
  onNewAutomation?: () => void
}

export function AutomationsEmptyState({ variant, hasAnyMoodboard, onNewAutomation }: Props) {
  if (variant === 'no-sources') {
    if (hasAnyMoodboard) {
      return (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
            <Hourglass size={20} className="text-muted-foreground/50" />
          </div>
          <div>
            <p className="text-foreground font-medium">Your moodboard is still being analyzed</p>
            <p className="text-muted-foreground mx-auto mt-1 max-w-sm text-sm">
              Once it's ready you can schedule automations.
            </p>
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
          <Palette size={20} className="text-muted-foreground/50" />
        </div>
        <div>
          <p className="text-foreground font-medium">Set up a moodboard first</p>
          <p className="text-muted-foreground mx-auto mt-1 max-w-sm text-sm">
            Automations draw on a moodboard for voice and style. Set one up, then come back to build
            a weekly cadence.
          </p>
        </div>
        <Button variant="primary" size="sm" asChild>
          <Link to="/moodboards">Set up a moodboard</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-5 py-12 text-center">
      <div
        aria-hidden
        className="border-border bg-card/50 w-full max-w-sm rounded-xl border border-dashed p-4 opacity-80">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm font-semibold">Every Monday at 8:00</span>
        </div>
        <div className="mt-3">
          <FlowRail muted platforms={['linkedin', 'instagram']} />
        </div>
      </div>

      <div>
        <p className="text-foreground font-medium">Put your posting on autopilot</p>
        <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
          Pick a day, a moodboard, and the platforms to post on. Cassi drafts a post each week and
          emails you when it's ready to review.
        </p>
      </div>
      <Button variant="primary" size="sm" onClick={onNewAutomation}>
        <Plus className="h-4 w-4" />
        New automation
      </Button>
    </div>
  )
}
